import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuditLogs } from "@/hooks/useAuditLogs";
import type { AuditAction } from "@/types";

const PAGE_LIMIT = 50;

// 한국어 매핑
const ACTION_LABELS: Record<AuditAction, string> = {
  create: "생성",
  update: "수정",
  delete: "삭제",
  delete_attempt: "삭제 시도(차단)",
  status_change: "상태 변경",
  login_success: "로그인 성공",
  login_failure: "로그인 실패",
  logout: "로그아웃",
  password_change: "비밀번호 변경",
  view: "열람",
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  case: "사건",
  sample: "시료",
  analysis: "분석",
  report: "보고서",
  user: "사용자",
  assessment: "판정",
  sample_file: "증거 원본 파일",
};

/**
 * 증거 원본 접근의 결과. `VIEW` 한 값이 **성공한 내려받기**와 **소실된 파일에
 * 대한 접근 시도** 둘 다를 뜻하기 때문에, 뱃지만으로는 정반대의 두 사건이
 * 같아 보인다. after_data.outcome 을 표에 끌어올려 구분한다.
 */
const EVIDENCE_OUTCOME_LABELS: Record<string, string> = {
  delivered: "내려받음",
  delivered_partial: "일부 내려받음",
  lost: "소실 — 제공 못 함",
};

/** 시료 삭제 시 원본이 어떻게 처분됐는지 (T4 tombstone). */
const EVIDENCE_DISPOSITION_LABELS: Record<string, string> = {
  retained_unreferenced: "보존됨(참조 없음)",
  already_lost: "이미 소실",
};

const ACTION_BADGE_COLORS: Record<AuditAction, string> = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  delete_attempt: "bg-orange-100 text-orange-800",
  status_change: "bg-purple-100 text-purple-800",
  login_success: "bg-teal-100 text-teal-800",
  login_failure: "bg-red-100 text-red-800",
  logout: "bg-gray-100 text-gray-700",
  password_change: "bg-yellow-100 text-yellow-800",
  view: "bg-gray-100 text-gray-600",
};

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function DataSummary({
  data,
  entityType,
}: {
  data: Record<string, unknown> | null;
  entityType?: string | null;
}): React.ReactElement {
  if (!data) return <span className="text-gray-400">—</span>;

  // 시료 삭제 시 남는 tombstone — outcome 키가 없어 아래 분기를 타지 못하고
  // 영문 스키마 키({stored_hash, stored_hash_scope, ...})로 찍히던 것.
  // 이 행이 답해야 할 질문은 "이 시료를 지웠을 때 원본은 어떻게 됐나" 이다.
  if (entityType === "sample_file" && Array.isArray(data.files)) {
    const files = data.files as { kind?: string; disposition?: string }[];
    const summary = files.length
      ? files
          .map(
            (f) =>
              `${f.kind ?? "?"} ${
                EVIDENCE_DISPOSITION_LABELS[f.disposition ?? ""] ??
                f.disposition ??
                "처분 불명"
              }`,
          )
          .join(" · ")
      : "원본 없음";
    return (
      <span
        className="text-xs text-gray-700"
        title={JSON.stringify(data, null, 2)}
      >
        삭제 시 처분: {summary}
      </span>
    );
  }

  // 증거 원본 행은 결과가 곧 사실이다 — 툴팁 뒤에 숨기지 않는다.
  if (entityType === "sample_file" && typeof data.outcome === "string") {
    const outcome = data.outcome as string;
    const isLost = outcome === "lost";
    return (
      <span
        className={`text-xs font-medium ${isLost ? "text-red-700" : "text-gray-700"}`}
        title={JSON.stringify(data, null, 2)}
      >
        {EVIDENCE_OUTCOME_LABELS[outcome] ?? outcome}
        {typeof data.file_name === "string" && (
          <span className="ml-1 font-normal text-gray-500">
            {data.file_name}
          </span>
        )}
      </span>
    );
  }

  const keys = Object.keys(data);
  const preview = keys.slice(0, 2).join(", ");
  return (
    <span
      className="text-xs text-gray-600 font-mono"
      title={JSON.stringify(data, null, 2)}
    >
      {`{${preview}${keys.length > 2 ? ", ..." : ""}}`}
    </span>
  );
}

export default function AdminAuditLogsPage() {
  const [filterEntityType, setFilterEntityType] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterEntityId, setFilterEntityId] = useState("");
  const [page, setPage] = useState(0);

  const filters = {
    entity_type: filterEntityType || undefined,
    action: filterAction || undefined,
    user_id: filterUserId ? Number(filterUserId) : undefined,
    entity_id: filterEntityId ? Number(filterEntityId) : undefined,
    skip: page * PAGE_LIMIT,
    limit: PAGE_LIMIT,
  };

  const { data, isLoading, error } = useAuditLogs(filters);

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const totalPages = Math.ceil(total / PAGE_LIMIT);
  const currentPage = page + 1;

  function handleFilterChange(setter: (v: string) => void) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setter(e.target.value);
      setPage(0);
    };
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Link
        to="/admin"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        ← 관리 홈
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          감사 로그 조회
        </h1>
        <p className="text-gray-600">
          시스템 내 주요 행위와 변경 이력을 확인할 수 있습니다.
        </p>
      </div>

      {/* 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label
              htmlFor="filter-entity-type"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              대상 유형
            </label>
            <select
              id="filter-entity-type"
              value={filterEntityType}
              onChange={handleFilterChange(setFilterEntityType)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              <option value="case">사건</option>
              <option value="sample">시료</option>
              <option value="analysis">분석</option>
              <option value="report">보고서</option>
              <option value="user">사용자</option>
              <option value="sample_file">증거 원본 파일</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="filter-action"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              액션
            </label>
            <select
              id="filter-action"
              value={filterAction}
              onChange={handleFilterChange(setFilterAction)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              {(Object.entries(ACTION_LABELS) as [AuditAction, string][]).map(
                ([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ),
              )}
            </select>
          </div>

          <div>
            <label
              htmlFor="filter-user-id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              사용자 ID
            </label>
            <input
              id="filter-user-id"
              type="number"
              value={filterUserId}
              onChange={handleFilterChange(setFilterUserId)}
              placeholder="숫자 입력"
              min={1}
              className="w-28 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="filter-entity-id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              대상 ID
            </label>
            <input
              id="filter-entity-id"
              type="number"
              value={filterEntityId}
              onChange={handleFilterChange(setFilterEntityId)}
              placeholder="숫자 입력"
              min={1}
              className="w-28 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setFilterEntityType("");
              setFilterAction("");
              setFilterUserId("");
              setFilterEntityId("");
              setPage(0);
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            필터 초기화
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">로그 목록</h2>
            {!isLoading && !error && (
              <p className="text-sm text-gray-500 mt-1">
                총 {total.toLocaleString("ko-KR")}건
                {totalPages > 1 && ` (${currentPage} / ${totalPages} 페이지)`}
              </p>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-gray-200 rounded mb-3" />
            ))}
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-600 font-medium">
              감사 로그를 불러오는 중 오류가 발생했습니다.
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              로그가 없습니다
            </p>
            <p className="text-sm text-gray-500">
              선택한 조건에 해당하는 감사 로그가 없습니다.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    시각
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    행위자(ID)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    대상
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    변경 전
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    변경 후
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {log.user_id !== null ? (
                        <span className="font-mono">{log.user_id}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${ACTION_BADGE_COLORS[log.action]}`}
                      >
                        {ACTION_LABELS[log.action] ?? log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {log.entity_type ? (
                        <>
                          <span className="font-medium">
                            {ENTITY_TYPE_LABELS[log.entity_type] ??
                              log.entity_type}
                          </span>
                          {log.entity_id !== null && (
                            <span className="text-gray-500 ml-1 font-mono">
                              {/* sample_file 의 entity_id 는 파일 id 가 아니라
                                  시료 id 다 — "#219" 로만 적으면 파일 219 로 읽힌다. */}
                              {log.entity_type === "sample_file"
                                ? `(시료 #${log.entity_id})`
                                : `#${log.entity_id}`}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {log.ip_address ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-sm max-w-xs">
                      <DataSummary data={log.before_data} />
                    </td>
                    <td className="px-4 py-3 text-sm max-w-xs">
                      <DataSummary
                        data={log.after_data}
                        entityType={log.entity_type}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이징 */}
        {!isLoading && !error && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {page * PAGE_LIMIT + 1}–{Math.min((page + 1) * PAGE_LIMIT, total)}
              건 / 전체 {total}건
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                이전
              </button>
              <span className="text-sm text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
